import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { LEAD_FORM } from "@/data/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;

const FIELDS = [
  { name: "name", placeholder: "Full Name*", type: "text", testId: "lead-form-name-input" },
  { name: "email", placeholder: "Email*", type: "email", testId: "lead-form-email-input" },
  { name: "phone", placeholder: "Phone*", type: "tel", testId: "lead-form-phone-input" },
  { name: "company", placeholder: "Company Name*", type: "text", testId: "lead-form-company-input" },
  { name: "website", placeholder: "Website*", type: "text", testId: "lead-form-website-input" },
];

const INITIAL = { name: "", email: "", phone: "", company: "", website: "" };

export const LeadForm = () => {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const validate = () => {
    const er = {};
    FIELDS.forEach((f) => {
      if (!form[f.name].trim()) er[f.name] = "Required";
    });
    if (form.email.trim() && !EMAIL_RE.test(form.email.trim())) {
      er.email = "Enter a valid email";
    }
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        interest: "Full Visibility Audit",
        source: "homepage-demo",
      });
      setSuccess(true);
      toast.success("Audit request received! We'll be in touch within 2 business days.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div
        data-testid="lead-form-success"
        className="rounded-[12px] bg-[rgb(239,238,238)] p-7 sm:p-8 text-center shadow-[0_10px_30px_rgba(2,2,2,0.08)]"
      >
        <CheckCircle2 size={44} strokeWidth={1.75} className="mx-auto text-[#ee2e24]" />
        <h4 className="mt-4 font-nunito text-lg font-extrabold text-[#383838]">Request received!</h4>
        <p className="mt-2 text-sm text-[#6e6e6e] leading-relaxed">
          A US-based strategist will review your visibility across Google, AI search and paid media
          — and get back to you within 2 business days.
        </p>
        <button
          type="button"
          data-testid="lead-form-submit-another-button"
          onClick={() => {
            setForm(INITIAL);
            setSuccess(false);
          }}
          className="mt-5 text-[13px] font-semibold text-[#ee2e24] hover:text-[#d92820] underline underline-offset-4 transition-colors duration-150"
        >
          Submit another request
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      data-testid="lead-form"
      className="rounded-[12px] bg-[rgb(239,238,238)] p-6 sm:p-7 shadow-[0_10px_30px_rgba(2,2,2,0.08)]"
    >
      <p className="text-[13px] text-[#6e6e6e] leading-relaxed mb-5">{LEAD_FORM.sub}</p>
      <div className="space-y-3.5">
        {FIELDS.map((f) => (
          <div key={f.name}>
            <label htmlFor={`lead-${f.name}`} className="sr-only">
              {f.placeholder.replace("*", "")}
            </label>
            <input
              id={`lead-${f.name}`}
              name={f.name}
              type={f.type}
              value={form[f.name]}
              onChange={onChange}
              placeholder={f.placeholder}
              data-testid={f.testId}
              aria-invalid={Boolean(errors[f.name])}
              className={`w-full rounded-[10px] border bg-white px-4 py-3 text-sm text-[#383838] placeholder:text-[#9a9a9a] transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-[rgba(238,46,36,0.35)] ${
                errors[f.name] ? "border-[#ee2e24]" : "border-[#e0e0e0] focus:border-[#ee2e24]"
              }`}
            />
            {errors[f.name] && (
              <p data-testid={`lead-form-${f.name}-error`} className="mt-1 text-xs font-medium text-[#ee2e24]">
                {errors[f.name]}
              </p>
            )}
          </div>
        ))}
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid="lead-form-submit-button"
        className="mt-5 w-full inline-flex justify-center items-center gap-2 rounded-[10px] bg-[#ee2e24] px-6 py-3.5 font-nunito text-[13px] font-extrabold uppercase tracking-[0.12em] text-white shadow-[0_10px_30px_rgba(238,46,36,0.3)] hover:bg-[#d92820] active:scale-[0.98] transition-colors duration-150 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[rgba(238,46,36,0.4)] focus:ring-offset-2"
      >
        {submitting && <Loader2 size={16} className="animate-spin" />}
        {submitting ? "Sending..." : LEAD_FORM.submitLabel}
      </button>

      <p className="mt-4 text-xs text-[#8a8a8a]">{LEAD_FORM.note}</p>
    </form>
  );
};
